import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Desktop Student Dashboard
export const DesktopStudentDashboard = () => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [attendanceResponse, announcementsResponse] = await Promise.all([
        axios.get(`${API}/attendance`),
        axios.get(`${API}/announcements`)
      ]);
      
      setAttendanceRecords(attendanceResponse.data);
      setAnnouncements(announcementsResponse.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Welcome back!</h2>
        <div className="flex justify-center items-center space-x-4">
          <span className="px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
            Regular Student
          </span>
          <span className="px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
            Yellow Belt
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Attendance History */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-blue-200 rounded-xl flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012-2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Your Attendance</h3>
              <p className="text-gray-600 text-sm">Recent class attendance records</p>
            </div>
          </div>

          {attendanceRecords.length > 0 ? (
            <div className="space-y-4">
              {attendanceRecords.slice(0, 5).map((record, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                      record.present ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {record.present ? (
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 capitalize">
                        {record.class_location.replace('_', ' ')} Class
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(record.class_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    record.present 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {record.present ? 'Present' : 'Absent'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No attendance records found</p>
          )}
        </div>

        {/* Recent Announcements */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 110 2h-1v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6H3a1 1 0 110-2h4z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Recent Announcements</h3>
              <p className="text-gray-600 text-sm">Latest updates from your instructors</p>
            </div>
          </div>

          {announcements.length > 0 ? (
            <div className="space-y-4">
              {announcements.slice(0, 3).map((announcement, index) => (
                <div key={index} className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-800">{announcement.title}</h4>
                  <p className="text-gray-600 text-sm mt-1">{announcement.content}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(announcement.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No announcements yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Desktop Announcements View
export const DesktopAnnouncementsView = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryInfo = (category) => {
    const categories = {
      general: { label: 'General News', color: 'blue', icon: '📢' },
      tournament: { label: 'Tournament Results', color: 'indigo', icon: '🏆' },
      achievement: { label: 'Student Achievements', color: 'green', icon: '⭐' },
      fee: { label: 'Fee Reminders', color: 'orange', icon: '💰' }
    };
    return categories[category] || categories.general;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Announcements</h2>
        <p className="text-gray-600">Latest news and updates from your karate classes</p>
      </div>

      {announcements.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {announcements.map((announcement) => {
            const categoryInfo = getCategoryInfo(announcement.category);
            
            return (
              <div key={announcement.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                {/* Category Header */}
                <div className={`px-4 py-2 ${
                  categoryInfo.color === 'blue' ? 'bg-blue-500' :
                  categoryInfo.color === 'indigo' ? 'bg-indigo-500' :
                  categoryInfo.color === 'green' ? 'bg-green-500' :
                  categoryInfo.color === 'orange' ? 'bg-orange-500' :
                  'bg-gray-500'
                }`}>
                  <div className="flex items-center text-white">
                    <span className="mr-2">{categoryInfo.icon}</span>
                    <span className="text-sm font-medium">{categoryInfo.label}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  {announcement.image_base64 && (
                    <img 
                      src={announcement.image_base64} 
                      alt="Announcement" 
                      className="w-full h-32 object-cover rounded-lg mb-3"
                    />
                  )}
                  
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    {announcement.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4">
                    {announcement.content}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {new Date(announcement.created_at).toLocaleDateString()}
                    </span>
                    <span>
                      {new Date(announcement.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 110 2h-1v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6H3a1 1 0 110-2h4z" />
          </svg>
          <p className="text-lg font-medium mb-2">No announcements yet</p>
          <p className="text-sm">Check back later for updates from your instructors.</p>
        </div>
      )}
    </div>
  );
};